import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Force and Translational Dynamics",
  examWeight: "18–23%",
  focus: "The interaction of objects and systems through forces, which cause changes in motion.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Systems and Center of Mass",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 Defining a System</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In physics, defining the <strong>system</strong> is the crucial first step in analysis.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Object Model</h4>
                <p className="text-sm text-slate-700 mt-1">If the internal structure and changes in shape of an object do not affect the analysis, it can be modeled as a point particle.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">System Types</h4>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-xs text-slate-700">
                  <li><strong>Open System:</strong> Can exchange mass and energy with surroundings.</li>
                  <li><strong>Closed System:</strong> Does not exchange mass with surroundings (energy might still transfer).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📍 Center of Mass (COM)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Center of Mass is a specific point that represents the average position of all the mass in a system.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">Calculation (Two objects/1D)</h4>
              <BlockMath math="x_{cm} = \frac{m_1x_1 + m_2x_2}{m_1 + m_2}" />
              <ul className="list-disc pl-5 mt-3 space-y-1 text-sm text-slate-700">
                <li>If masses are equal, COM is exactly in the middle.</li>
                <li>If masses are unequal, COM is closer to the heavier mass.</li>
              </ul>
            </div>
            <ImportantNote>
              <strong>Significance:</strong> When a net external force acts on a system, the COM moves as if all the system's mass were concentrated at that point (e.g., a spinning thrown baton traces a perfect parabola at its COM).
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Internal vs. External Forces</h3>
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Internal Forces</h4>
                <p className="text-sm text-slate-600">Forces between objects inside the chosen system. By Newton’s 3rd Law, these sum to zero and <strong>cannot</strong> change the velocity of the system's COM.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">External Forces</h4>
                <p className="text-sm text-slate-600">Forces exerted on the system by the environment. Only net external forces cause the COM to accelerate.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Forces and Free-Body Diagrams",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚛️ The Nature of Force</h3>
            <p className="text-slate-700 mb-4">A force is a push or pull resulting from the <strong>interaction</strong> between two objects.</p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm term="Contact Forces" definition="Physical touching required (e.g., Normal, Tension, Friction, Applied)." />
              <KeyTerm term="Field Forces" definition="Act over a distance (e.g., Gravitational, Electric, Magnetic)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📝 Free-Body Diagrams (FBD)</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900 mb-2">Rules for Drawing (Crucial for AP Exams)</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li>Represent the object as a <strong>dot</strong> (or box).</li>
                <li>Draw arrows originating <strong>from the dot</strong> pointing outward in the direction of the force.</li>
                <li><strong>Label</strong> every vector (e.g., <InlineMath math="F_g, F_N, F_T" />).</li>
                <li>Draw only forces exerted <strong>ON</strong> the object.</li>
                <li><strong>Do not</strong> draw components on the main FBD; keep them on a separate scratchpad diagram.</li>
              </ol>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Coordinate Systems & Ramps</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Choose a coordinate system that simplifies the math. For ramps/inclines, <strong>tilt the axes</strong>.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-2">Gravity Components on Incline:</h4>
              <ul className="space-y-2 text-sm text-slate-700">
                <li><InlineMath math="F_{g,\parallel} = mg\sin(\theta)" /> — Force pulling down the ramp.</li>
                <li><InlineMath math="F_{g,\perp} = mg\cos(\theta)" /> — Force pressing into the ramp.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Newton’s Third Law",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 The Law of Interaction</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <BlockMath math="\vec{F}_{A \text{ on } B} = -\vec{F}_{B \text{ on } A}" />
              <h4 className="font-bold text-purple-900 mt-4 mb-2">Key Characteristics of Action-Reaction Pairs:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li>Equal Magnitude and Opposite Direction.</li>
                <li>Act on <strong>different objects</strong> (they never cancel each other out on a single FBD).</li>
                <li>Are the <strong>same type</strong> of force.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚛 Common Misconceptions</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-900">Scenario: Semi-truck vs. Mosquito</h4>
              <p className="text-sm text-slate-700 mt-1">The force of the truck on the mosquito is <strong>equal</strong> to the force of the mosquito on the truck (Newton's 3rd). The accelerations differ solely because their masses are vastly different (Newton's 2nd).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧵 Tension</h3>
            <p className="text-slate-700 text-sm mb-3">Tension is the pulling force transmitted axially by a string, cable, or chain.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs"><strong>Ideal Strings:</strong> Massless and do not stretch. Tension is uniform throughout.</p>
              </div>
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs"><strong>Ideal Pulleys:</strong> Simply change the direction of the tension force without changing magnitude.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Newton’s First Law",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Inertia and Equilibrium</h3>
            <KeyTerm term="Inertia" definition="The resistance of an object to changes in its state of motion. Mass is the measure of inertia." />
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900 text-sm">Static Equilibrium</h4>
                <p className="text-xs text-slate-700 mt-1">Velocity is zero (at rest). <InlineMath math="\Sigma \vec{F} = 0" /></p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900 text-sm">Dynamic Equilibrium</h4>
                <p className="text-xs text-slate-700 mt-1">Velocity is constant (straight line, constant speed). <InlineMath math="\Sigma \vec{F} = 0" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎥 Inertial Reference Frames</h3>
            <p className="text-slate-700 text-sm mb-4">A viewpoint (frame) that is not accelerating. Newton’s Laws only hold true here.</p>
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-300">
              <p className="text-sm text-slate-700"><strong>Example:</strong> In a car turning left, you feel pushed right. This "push" is a <em>fictitious force</em> caused by your own inertia, as viewed from a non-inertial (accelerating) frame.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Newton’s Second Law",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 The Law of Acceleration</h3>
            <div className="bg-slate-800 p-6 rounded-xl text-white text-center shadow-lg">
              <BlockMath math="\vec{a} = \frac{\Sigma \vec{F}}{m} \quad \text{or} \quad \Sigma \vec{F} = m\vec{a}" />
              <p className="text-xs text-slate-400 mt-2 italic">Vector Nature: Applies to components independently (<InlineMath math="\Sigma F_x = ma_x" />, <InlineMath math="\Sigma F_y = ma_y" />).</p>
            </div>
          </section>

          <Mnemonic title="Problem Solving (4-Step)" content="Sketch (Visualize) → FBD (Draw forces) → Newton's 2nd Law (Write ΣF = ma) → Solve (Algebra)." />

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 System Approach (Coupled Objects)</h3>
            <div className="space-y-3">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-bold text-blue-900">Treat as one system</h4>
                <p className="text-sm text-slate-700"><InlineMath math="\Sigma F_{external} = (m_{total})a_{system}" /></p>
                <p className="text-xs text-slate-500 mt-1">Internal forces (tension between blocks) cancel out.</p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-slate-800">Treat individually</h4>
                <p className="text-sm text-slate-700">Apply Newton's 2nd Law to just one object after finding system acceleration to find internal forces (like tension).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Gravitational Force",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌌 Universal Gravitation</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <BlockMath math="|F_g| = G\frac{m_1 m_2}{r^2}" />
              <p className="text-xs text-slate-600 mt-2 text-center"><InlineMath math="G \approx 6.67 \times 10^{-11} \, \text{N}\cdot\text{m}^2/\text{kg}^2" /></p>
            </div>
            <KeyTerm term="Inverse Square Law" definition="If you double the distance r, the force drops by a factor of 4 (1/2²)." />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Gravitational Field (g)</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Field Strength Equation:</h4>
                <BlockMath math="g = \frac{F_g}{m_{test}} = \frac{G M_{source}}{r^2}" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 border rounded-lg">
                  <p className="text-xs"><strong>Near Earth:</strong> <InlineMath math="r" /> changes little, so <InlineMath math="g \approx 9.8" /> (or 10) m/s².</p>
                </div>
                <div className="p-3 bg-slate-50 border rounded-lg">
                  <p className="text-xs"><strong>Weight:</strong> The specific force of gravity on an object (<InlineMath math="F_g = mg" />).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Mass Equivalence</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
              <li><strong>Gravitational Mass:</strong> Determined by measuring force of gravity (<InlineMath math="m = F_g/g" />).</li>
              <li><strong>Inertial Mass:</strong> Determined by measuring acceleration under force (<InlineMath math="m = F/a" />).</li>
              <li><strong>Equivalence Principle:</strong> Experiments show these two masses are numerically identical.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Kinetic and Static Friction",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 The Nature of Friction</h3>
            <p className="text-slate-700 text-sm mb-4">Opposes relative motion. Depends on Normal Force (<InlineMath math="F_N" />) and surface roughness (<InlineMath math="\mu" />).</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Kinetic Friction (<InlineMath math="f_k" />)</h4>
                <p className="text-xs text-slate-700 mt-1 mb-2">Surfaces are <strong>sliding</strong>.</p>
                <BlockMath math="|F_{f,k}| = \mu_k |F_N|" />
              </div>
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">Static Friction (<InlineMath math="f_s" />)</h4>
                <p className="text-xs text-slate-700 mt-1 mb-2">Surfaces are <strong>not sliding</strong>.</p>
                <BlockMath math="|F_{f,s}| \le \mu_s |F_N|" />
              </div>
            </div>
            <ImportantNote>
              Static friction is a <strong>"smart" force</strong>. It adjusts to match the applied force until it reaches its maximum limit. Generally, <InlineMath math="\mu_s > \mu_k" /> (harder to start than to keep moving).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Spring Forces",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">➰ Hooke’s Law</h3>
            <div className="bg-slate-50 p-5 rounded-lg border border-slate-300">
              <BlockMath math="|\vec{F}_s| = k|\vec{x}|" />
              <div className="grid grid-cols-2 gap-4 mt-4 text-xs text-slate-700">
                <p><strong>k:</strong> Spring constant (stiffness, N/m).</p>
                <p><strong>x:</strong> Displacement from equilibrium.</p>
              </div>
              <p className="text-xs text-slate-500 mt-3 italic">Direction: The force always points toward the equilibrium position (opposite to displacement).</p>
            </div>
          </section>
          <div className="grid grid-cols-2 gap-3">
            <KeyTerm term="Stiff Spring" definition="High k; requires more force to stretch." />
            <KeyTerm term="Loose Spring" definition="Low k." />
          </div>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Circular Motion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Uniform Circular Motion (UCM)</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
              <li><strong>Velocity:</strong> Magnitude is constant, but <strong>direction is changing</strong>.</li>
              <li><strong>Acceleration:</strong> Points toward the center (Centripetal).</li>
            </ul>
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200 mt-4">
              <h4 className="font-bold text-rose-900 text-sm">Equations:</h4>
              <div className="flex justify-around items-center py-2">
                <InlineMath math="a_c = \frac{v^2}{r}" />
                <InlineMath math="\Sigma F_radial = \frac{mv^2}{r}" />
              </div>
            </div>
          </section>

          <div className="bg-red-600 p-4 rounded-lg text-white shadow-md">
            <h4 className="font-bold uppercase text-xs tracking-widest mb-1">Critical Concept</h4>
            <p className="text-sm">Centripetal force is <strong>NOT</strong> a new force. It is a label for the <strong>Net Force</strong> provided by physical sources (Gravity, Tension, Friction) to keep an object in a circle.</p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎡 Vertical Circles</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-800 text-sm">Top of loop (inverted)</h4>
                <p className="text-xs text-slate-600 mt-1"><InlineMath math="F_N + F_g = \frac{mv^2}{r}" /></p>
                <p className="text-xs font-bold mt-2 text-indigo-700">Critical Speed: <InlineMath math="v_{min} = \sqrt{gr}" /> (when <InlineMath math="F_N \to 0" />)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-800 text-sm">Bottom of loop</h4>
                <p className="text-xs text-slate-600 mt-1"><InlineMath math="F_N - F_g = \frac{mv^2}{r}" /></p>
                <p className="text-xs text-slate-500 mt-1 italic">Normal force must be larger than gravity to accelerate upward/inward.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default physicsUnit2Notes;